import { Injectable, inject, signal, computed } from '@angular/core';
import { MetaMasivaRepository } from 'src/app/domain/service/meta-masiva/meta-masiva-repository';
import {
  MetaMasiva,
  MetaMasivaDireccion
} from 'src/app/domain/model/meta-masiva/meta-masiva';
import { Oficina } from 'src/app/domain/model/catalogo/oficina';
import { TipoAsesorReal } from 'src/app/domain/model/catalogo/tipo-asesor-real';
import { HttpOficinaRepository } from 'src/app/infrastructure/adapters/catalogo/http-oficina.repository';
import { HttpTipoAsesorRealRepository } from 'src/app/infrastructure/adapters/catalogo/http-tipo-asesor-real.repository';
import {
  META_MASIVA_ANIO_MAX_LENGTH,
  META_MASIVA_ANIO_REGEX,
  META_MASIVA_META_MES_MAX_LENGTH,
  META_MASIVA_META_MES_REGEX,
  META_MASIVA_PAGE_SIZE,
  MENSAJE_FALLA_TECNICA
} from 'src/app/common/constants-ejb';
import { sanitizeText } from 'src/app/common/input-utils';

export type MetaMensajeTipo = 'success' | 'info' | 'warning' | 'error';

/** Valores que entrega el modal "Nueva Meta Masiva" al pulsar Grabar. */
export interface MetaMasivaFormulario {
  mes: number | null;
  anio: string;
  oficinas: number[];
  tipoAsesor: string | null;
  metaMes: string;
}

/** Filtros del listado: oficina elegida y año tal como lo escribe el usuario. */
export interface MetasFiltro {
  oficina: number | null;
  anio: string;
}

/**
 * Facade de "Administración › Maestros › Metas" — Asignación Masiva de Metas Mes (HU1290096).
 *
 * <p>Maneja el estado del listado (filtros, resultados, paginación por cursor), la edición en celda
 * de la Meta Mes y el modal "Nueva Meta Masiva". Expone solo estado (signals) y métodos al container
 * (patrón Facade + Container/Dumb, frontend-girop §14).</p>
 *
 * <p>Legacy: ListarMetasAnioViewImpl / ListarMetasAnioActivity (listado y modificación) y
 * NuevaMetaMasivaViewImpl / NuevaMetaMasivaActivity (modal). Los mensajes son literales del legacy; la
 * falla técnica usa el mensaje genérico sin detalle del servidor (RN-15).</p>
 */
@Injectable()
export class MetasFacade {
  static readonly MSG_SIN_RESULTADOS =
    'No se recuperó información con los parámetros ingresados.';
  static readonly MSG_ANIO_VACIO = 'Se debe ingresar un año para la consulta';
  static readonly MSG_ANIO_FORMATO = 'El año no tiene un formato valido';
  static readonly MSG_CAMPOS_INVALIDOS =
    'Por favor ingrese correctamente los siguientes campos';
  static readonly MSG_ACTUALIZACION_EXITOSA =
    'Los datos se actualizaron correctamente.';
  static readonly MSG_FORMATO_NUMERICO =
    'El campo numérico no tiene un formato valido';
  static readonly MSG_META_VACIA = 'Debe ingresar una Meta para el Mes';
  static readonly MSG_ANIO_MODAL_VACIO =
    'Debe ingresar una Año para la Meta Masiva';
  static readonly MSG_MES_MODAL = 'Debe seleccionar un Mes para la Meta Masiva';
  static readonly MSG_TIPO_ASESOR_MODAL =
    'Debe seleccionar un Tipo de Asesor para la Meta Masiva';
  static readonly MSG_OFICINAS_MODAL =
    'Debe elegir al menos una oficina para la Meta';
  static readonly CAMPO_OFICINA = 'Oficina';
  static readonly CAMPO_ANIO = 'Ingrese el Año';

  private readonly repository = inject(MetaMasivaRepository);
  private readonly oficinaRepository = inject(HttpOficinaRepository);
  private readonly tipoAsesorRepository = inject(HttpTipoAsesorRealRepository);

  // ==================== Catálogos (solo activos, RN-14) ====================

  readonly oficinas = signal<Oficina[]>([]);
  readonly tiposAsesor = signal<TipoAsesorReal[]>([]);

  // ==================== Filtros ====================

  filtro: MetasFiltro = { oficina: null, anio: '' };

  // ==================== Resultado / estado ====================

  readonly items = signal<MetaMasiva[]>([]);
  readonly loading = signal(false);
  readonly busquedaRealizada = signal(false);
  readonly mensaje = signal('');
  readonly mensajeTipo = signal<MetaMensajeTipo>('info');
  /** Consecutivo de la fila cuya Meta Mes se está editando en la celda. */
  readonly editandoId = signal<number | null>(null);

  // ==================== Paginación cursor (server-side) ====================

  readonly currentPage = signal(1);
  /** El legacy muestra 10 registros por página (RN-16); no es configurable en pantalla. */
  readonly pageSize = signal(META_MASIVA_PAGE_SIZE);
  readonly totalElements = signal(0);
  readonly hasNext = signal(false);
  readonly hasPrevious = signal(false);
  private nextCursorActual: string | null = null;
  private previousCursorActual: string | null = null;
  /** Filtros con los que se hizo la última consulta (la paginación y la recarga no dependen de lo editado). */
  private consultaActual: { oficina: number; anio: number } | null = null;
  /** Cursor y sentido de la página mostrada, para recargarla tras modificar. */
  private paginaActual: {
    cursor: string | null;
    direction: MetaMasivaDireccion;
  } = { cursor: null, direction: 'NEXT' };

  readonly totalPages = computed(
    () => Math.ceil(this.totalElements() / this.pageSize()) || 1
  );
  readonly registroInicio = computed(() => {
    if (this.totalElements() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });
  readonly registroFin = computed(() =>
    Math.min(this.currentPage() * this.pageSize(), this.totalElements())
  );

  // ==================== Modal Nueva Meta Masiva ====================

  readonly dialogoVisible = signal(false);
  readonly guardando = signal(false);
  /** Errores del modal; se muestran juntos y en el orden del legacy. */
  readonly mensajesDialogo = signal<string[]>([]);

  // ==================== Inicialización ====================

  /** Carga las oficinas y los tipos de asesor activos para los combos y las casillas. */
  async inicializar(): Promise<void> {
    try {
      const [oficinas, tiposAsesor] = await Promise.all([
        this.oficinaRepository.listarActivas(),
        this.tipoAsesorRepository.listarActivos()
      ]);
      this.oficinas.set(oficinas);
      this.tiposAsesor.set(tiposAsesor);
    } catch {
      this.mostrarMensaje(MENSAJE_FALLA_TECNICA, 'error');
    }
  }

  // ==================== Consulta ====================

  async buscar(): Promise<void> {
    if (this.loading()) return;
    this.editandoId.set(null);

    const anio = sanitizeText(this.filtro.anio).trim();
    if (anio.length === 0) {
      this.reiniciarResultados();
      this.mostrarMensaje(MetasFacade.MSG_ANIO_VACIO, 'error');
      return;
    }
    if (!this.anioValido(anio)) {
      this.reiniciarResultados();
      this.mostrarMensaje(MetasFacade.MSG_ANIO_FORMATO, 'error');
      return;
    }
    const oficina = this.filtro.oficina;
    if (!this.filtrosCompletos() || oficina === null) {
      this.reiniciarResultados();
      return;
    }

    this.consultaActual = { oficina, anio: Number(anio) };
    this.currentPage.set(1);
    await this.cargarPagina(null, 'NEXT');
  }

  /** Legacy: reset() — año vacío, oficina sin selección, tabla oculta y sin mensajes. */
  limpiarFiltros(): void {
    this.filtro = { oficina: null, anio: '' };
    this.reiniciarResultados();
    this.mensaje.set('');
  }

  irPaginaAnterior(): void {
    if (!this.hasPrevious() || this.loading()) return;
    this.currentPage.update((p) => Math.max(1, p - 1));
    void this.cargarPagina(this.previousCursorActual, 'PREVIOUS');
  }

  irPaginaSiguiente(): void {
    if (!this.hasNext() || this.loading()) return;
    this.currentPage.update((p) => p + 1);
    void this.cargarPagina(this.nextCursorActual, 'NEXT');
  }

  private async cargarPagina(
    cursor: string | null,
    direction: MetaMasivaDireccion,
    conservarMensaje = false
  ): Promise<void> {
    const consulta = this.consultaActual;
    if (!consulta) {
      this.loading.set(false);
      return;
    }
    this.loading.set(true);
    if (!conservarMensaje) this.mensaje.set('');
    try {
      const response = await this.repository.listar(
        consulta,
        this.pageSize(),
        cursor,
        direction
      );

      this.paginaActual = { cursor, direction };
      this.items.set(response.content);
      this.totalElements.set(response.totalElements ?? response.content.length);
      this.nextCursorActual = response.nextCursor ?? null;
      this.previousCursorActual = response.previousCursor ?? null;
      this.hasNext.set(response.hasNext);
      this.hasPrevious.set(response.hasPrevious);
      this.busquedaRealizada.set(true);

      if (response.content.length === 0) {
        // Legacy: cons.busquedaNoArrojoResultado() — la tabla queda oculta.
        this.reiniciarResultados();
        this.mostrarMensaje(MetasFacade.MSG_SIN_RESULTADOS, 'warning');
      }
    } catch {
      // Legacy: cons.genericError() — sin detalle técnico (RN-15).
      this.reiniciarResultados();
      this.mostrarMensaje(MENSAJE_FALLA_TECNICA, 'error');
    } finally {
      this.loading.set(false);
    }
  }

  // ==================== Edición en celda de Meta Mes ====================

  iniciarEdicion(meta: MetaMasiva): void {
    this.editandoId.set(meta.nmconsecutivo);
  }

  cancelarEdicion(): void {
    this.editandoId.set(null);
  }

  /**
   * Confirma la edición de la Meta Mes de una fila (Escenarios 10 a 14).
   *
   * <p>Con formato inválido o con filtros vacíos no se envía nada y la celda sigue en edición. Si el
   * procedimiento devuelve un mensaje de usuario se muestra como advertencia; en ambos casos se
   * recarga la tabla.</p>
   */
  async modificarMeta(meta: MetaMasiva, valor: string): Promise<void> {
    if (this.loading()) return;
    const texto = sanitizeText(valor).trim();
    if (!this.metaMesValida(texto)) {
      this.mostrarMensaje(MetasFacade.MSG_FORMATO_NUMERICO, 'error');
      return;
    }
    if (!this.filtrosCompletos()) return;

    this.loading.set(true);
    try {
      const resultado = await this.repository.modificar(
        meta.nmconsecutivo,
        Number(texto)
      );
      this.editandoId.set(null);
      this.mostrarResultado(resultado.exito, resultado.mensaje);
    } catch {
      this.editandoId.set(null);
      this.reiniciarResultados();
      this.mostrarMensaje(MENSAJE_FALLA_TECNICA, 'error');
      this.loading.set(false);
      return;
    }
    // La recarga apaga `loading` al terminar y conserva el mensaje del resultado.
    await this.cargarPagina(
      this.paginaActual.cursor,
      this.paginaActual.direction,
      true
    );
  }

  // ==================== Modal Nueva Meta Masiva ====================

  abrirNuevo(): void {
    this.mensajesDialogo.set([]);
    this.dialogoVisible.set(true);
  }

  cerrarDialogo(): void {
    this.dialogoVisible.set(false);
    this.mensajesDialogo.set([]);
  }

  limpiarMensajesDialogo(): void {
    this.mensajesDialogo.set([]);
  }

  /**
   * Valida y graba (Escenarios 2, 3, 15 a 17). Con datos faltantes o inválidos el modal permanece
   * abierto con todos los errores juntos. Tras grabar, o si el procedimiento devuelve un mensaje, el
   * modal se cierra, el mensaje se muestra en el listado y la tabla queda oculta.
   */
  async grabar(formulario: MetaMasivaFormulario): Promise<void> {
    if (this.guardando()) return;
    const anio = sanitizeText(formulario.anio).trim();
    const metaMes = sanitizeText(formulario.metaMes).trim();

    const { mes, tipoAsesor } = formulario;
    const errores = this.erroresModal(formulario, anio, metaMes);
    if (errores.length > 0 || mes === null || !tipoAsesor) {
      this.mensajesDialogo.set(errores);
      return;
    }

    this.guardando.set(true);
    this.mensajesDialogo.set([]);
    try {
      const resultado = await this.repository.crear({
        mes,
        anio: Number(anio),
        oficinas: [...formulario.oficinas],
        tipoAsesor,
        metaMes: Number(metaMes)
      });
      this.cerrarDialogo();
      this.reiniciarResultados();
      this.editandoId.set(null);
      this.mostrarResultado(resultado.exito, resultado.mensaje);
    } catch {
      // Todo o nada (RN-13): no quedó nada grabado; el modal sigue abierto para reintentar.
      this.mensajesDialogo.set([MENSAJE_FALLA_TECNICA]);
    } finally {
      this.guardando.set(false);
    }
  }

  // ==================== Validaciones ====================

  /** Errores del modal en el orden del legacy: meta, año, mes, tipo de asesor y oficinas. */
  private erroresModal(
    formulario: MetaMasivaFormulario,
    anio: string,
    metaMes: string
  ): string[] {
    const errores: string[] = [];
    if (metaMes.length === 0) {
      errores.push(MetasFacade.MSG_META_VACIA);
    } else if (!this.metaMesValida(metaMes)) {
      errores.push(MetasFacade.MSG_FORMATO_NUMERICO);
    }
    if (anio.length === 0) {
      errores.push(MetasFacade.MSG_ANIO_MODAL_VACIO);
    } else if (!this.anioValido(anio)) {
      errores.push(MetasFacade.MSG_FORMATO_NUMERICO);
    }
    if (formulario.mes === null || formulario.mes === undefined) {
      errores.push(MetasFacade.MSG_MES_MODAL);
    }
    if (!formulario.tipoAsesor) {
      errores.push(MetasFacade.MSG_TIPO_ASESOR_MODAL);
    }
    if (formulario.oficinas.length === 0) {
      errores.push(MetasFacade.MSG_OFICINAS_MODAL);
    }
    return errores;
  }

  private anioValido(anio: string): boolean {
    return (
      anio.length <= META_MASIVA_ANIO_MAX_LENGTH &&
      META_MASIVA_ANIO_REGEX.test(anio)
    );
  }

  /** Obligatoria, hasta 6 caracteres con 3 enteros y 2 decimales (RN-09); "." solo no es un número. */
  private metaMesValida(valor: string): boolean {
    return (
      valor.length > 0 &&
      valor.length <= META_MASIVA_META_MES_MAX_LENGTH &&
      META_MASIVA_META_MES_REGEX.test(valor) &&
      Number.isFinite(Number(valor))
    );
  }

  /** Oficina y año son obligatorios al consultar y al modificar (Escenarios 8 y 13). */
  private filtrosCompletos(): boolean {
    const faltantes: string[] = [];
    if (this.filtro.oficina === null || this.filtro.oficina === undefined) {
      faltantes.push(MetasFacade.CAMPO_OFICINA);
    }
    if (sanitizeText(this.filtro.anio).trim().length === 0) {
      faltantes.push(MetasFacade.CAMPO_ANIO);
    }
    if (faltantes.length === 0) return true;
    this.mostrarMensaje(
      `${MetasFacade.MSG_CAMPOS_INVALIDOS}\n${faltantes.join('\n')}`,
      'warning'
    );
    return false;
  }

  // ==================== Utilidades ====================

  private mostrarMensaje(texto: string, tipo: MetaMensajeTipo): void {
    this.mensaje.set(texto);
    this.mensajeTipo.set(tipo);
  }

  /**
   * Éxito: "Los datos se actualizaron correctamente."; si el procedimiento devolvió un mensaje de
   * usuario (`exito = false`) se muestra como advertencia (RN-07). Sin texto se usa la falla técnica.
   */
  private mostrarResultado(exito: boolean, mensaje: string | null): void {
    if (exito) {
      this.mostrarMensaje(MetasFacade.MSG_ACTUALIZACION_EXITOSA, 'success');
    } else if (mensaje) {
      this.mostrarMensaje(mensaje, 'warning');
    } else {
      this.mostrarMensaje(MENSAJE_FALLA_TECNICA, 'error');
    }
  }

  /** Oculta la tabla y vuelve a la primera página, como el `setVisible(false)` del legacy. */
  private reiniciarResultados(): void {
    this.items.set([]);
    this.totalElements.set(0);
    this.busquedaRealizada.set(false);
    this.currentPage.set(1);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.nextCursorActual = null;
    this.previousCursorActual = null;
    this.consultaActual = null;
    this.paginaActual = { cursor: null, direction: 'NEXT' };
  }
}
